/** Email auth tabs, Google sign-in, and sign-in/up forms on the login page. */
import { useT } from '@/i18n/useT';
import {
  MODE_SIGN_IN,
  MODE_SIGN_UP,
  useLoginEmailAuth,
} from '@/features/auth/useLoginEmailAuth';
import { LoginEmailAuthPanelBody } from '@/features/auth/loginEmailAuthPanelBody';

/**
 * @param {{
 *   onGoogleSignIn: () => void,
 *   googleErrorKey: string | null,
 *   onPanelInteraction?: () => void,
 * }} props
 */
const LoginEmailAuthPanel = ({
  onGoogleSignIn,
  googleErrorKey,
  onPanelInteraction,
}) => {
  const t = useT();
  const auth = useLoginEmailAuth({ onPanelInteraction });

  const signInSelected = auth.mode === MODE_SIGN_IN;
  const panelId = signInSelected ? 'login-panel-signin' : 'login-panel-signup';
  const googleLabel = signInSelected
    ? t('login.googleSignIn', 'Sign in with Google')
    : t('login.googleSignUp', 'Sign up with Google');

  const handleTabListKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'End') {
      event.preventDefault();
      auth.switchMode(MODE_SIGN_UP);
      document.getElementById('login-tab-signup')?.focus();
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'Home') {
      event.preventDefault();
      auth.switchMode(MODE_SIGN_IN);
      document.getElementById('login-tab-signin')?.focus();
    }
  };

  return (
    <section
      className="login-email-auth"
      data-testid="login-email-auth"
      aria-labelledby="login-email-heading"
    >
      <p id="login-email-heading" className="login-email-auth-sr-only">
        {t('login.tabsLabel', 'Email sign-in options')}
      </p>
      <div
        role="tablist"
        aria-label={t('login.tabsLabel', 'Email sign-in options')}
        className="login-tabs"
        onKeyDown={handleTabListKeyDown}
      >
        <button
          type="button"
          role="tab"
          id="login-tab-signin"
          aria-selected={signInSelected ? 'true' : 'false'}
          aria-controls={panelId}
          tabIndex={signInSelected ? 0 : -1}
          onClick={() => auth.switchMode(MODE_SIGN_IN)}
        >
          {t('login.tabSignIn', 'Sign in')}
        </button>
        <button
          type="button"
          role="tab"
          id="login-tab-signup"
          aria-selected={!signInSelected ? 'true' : 'false'}
          aria-controls={panelId}
          tabIndex={!signInSelected ? 0 : -1}
          onClick={() => auth.switchMode(MODE_SIGN_UP)}
        >
          {t('login.tabSignUp', 'Sign up')}
        </button>
      </div>
      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={signInSelected ? 'login-tab-signin' : 'login-tab-signup'}
      >
        <LoginEmailAuthPanelBody
          auth={auth}
          signInSelected={signInSelected}
          googleLabel={googleLabel}
          onGoogleSignIn={onGoogleSignIn}
          googleErrorKey={googleErrorKey}
          t={t}
        />
      </div>
    </section>
  );
};

export default LoginEmailAuthPanel;
